import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { reviewAPI } from '../services/api';
import { API_BASE_URL } from '../services/api';
import '../ResultAndRevision.scss';

// Import utilities from copied modules  
import { 
  isGCSECategory, 
  isOralCategory, 
  isEnglishCategory, 
  isGCSELanguageCategory,
  getCategorySubject 
} from '../utils/categoryHelpers.js';

import {
  colorMapEnglish,
  colorMapAiste, 
  colorMapGCSE,
  colorMapGCSELang,
  colorMapFrench,
  colorMapGerman,
  colorMapCombined,
  colorMapCombinedWithGCSELang,
  getColorMapForCategory,
  getValidCodesForCategory
} from '../utils/colorMaps.js';

import {
  isJsonResponse,
  parseJsonResponse,
  parseLineByLine,
  parseForRevision
} from '../utils/feedbackParsers.js';

import {
  applyInlineAnnotations,
  stripInlineAnnotations,
  formatFeedbackContent,
  processInlineFeedback
} from '../utils/annotationHelpers.js';

import {
  formatGradeText,
  getWordCountWarning,
  jsonGradesToText
} from '../utils/gradeFormatters.js';

import { getCategoryGradeConfig } from '../utils/categoryGradeConfig.js';

// Import copied components
import JsonFeedback from '../components/JsonFeedback';
import JsonGrades from '../components/JsonGrades';

// Import custom hooks
import { useFeedbackRatings } from '../hooks/useFeedbackRatings.js';
import { useFeedbackEditor } from '../hooks/useFeedbackEditor.js';

// Style constants (copied from main app)
const commonInputStyle = {
  width: '100%',
  padding: '12px 16px',
  marginBottom: '15px',
  borderRadius: '8px',
  border: '1px solid #cbd5e1',
  fontSize: '16px',
  transition: 'all 0.2s ease',
  boxSizing: 'border-box',
  backgroundColor: 'white',
  color: '#334155',
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  resize: 'vertical'
};

const inputHoverStyle = {
  borderColor: '#94a3b8',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
};

const inputFocusStyle = {
  outline: 'none',
  borderColor: '#2980b9',
  boxShadow: '0 0 0 3px rgba(41, 128, 185, 0.1)'
};

const commonButtonStyle = {
  padding: '12px 24px',
  fontSize: '16px',
  backgroundColor: '#2980b9',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontWeight: '500'
};

const secondaryButtonStyle = {
  ...commonButtonStyle,
  backgroundColor: '#6c757d'
};

const TrainingEssayReview = () => {
  const { user, authToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { trainingId } = useParams();
  
  // Main state
  const [essay, setEssay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [answer, setAnswer] = useState('');
  const [question, setQuestion] = useState('');
  const [category, setCategory] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [taskId, setTaskId] = useState(null);
  const [autoGrading, setAutoGrading] = useState(false);
  
  // JSON parsing state
  const [isJson, setIsJson] = useState(false);
  const [jsonData, setJsonData] = useState(null);
  const [parsedFeedback, setParsedFeedback] = useState([]);
  const [feedbackType, setFeedbackType] = useState('inline');
  
  // Grade editing state
  const [isEditingGrade, setIsEditingGrade] = useState(false);
  const [editedGradeText, setEditedGradeText] = useState('');
  const [gradeSaveStatus, setGradeSaveStatus] = useState(null);
  const [displayedGradeText, setDisplayedGradeText] = useState('');
  const [editedGrades, setEditedGrades] = useState({});
  const [isStructuredEdit, setIsStructuredEdit] = useState(false);
  const [localJsonData, setLocalJsonData] = useState(null);

  // Use hooks exactly like main app
  const {
    commentRatings,
    setCommentRatings,
    handleCommentRating: handleCommentRatingHook
  } = useFeedbackRatings(taskId, authToken, API_BASE_URL);

  const {
    // Feedback editing state
    isEditingFeedback,
    editableFeedback,
    updatedFeedbackContent,
    setUpdatedFeedbackContent,
    feedbackUpdateStatus,
    isAddingFeedback,
    addFeedbackParaIndex,
    newFeedbackContent,
    setNewFeedbackContent,
    newFeedbackType,
    setNewFeedbackType,
    // Feedback editing functions
    handleEditFeedback,
    handleSaveEditedFeedback,
    handleCancelEdit,
    handleDeleteFeedback,
    handleAddFeedback,
    handleSaveNewFeedback,
    handleCancelAddFeedback,
    getFeedbackTypeOptions
  } = useFeedbackEditor(
    category,
    user, // userData
    taskId,
    authToken,
            API_BASE_URL, // API_BASE_URL
    result,
    localJsonData,
    setLocalJsonData,
    parsedFeedback,
    setParsedFeedback,
    commentRatings,
    setCommentRatings
  );

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && authToken && trainingId) {
      loadEssay();
    }
  }, [user, authToken, trainingId]);

  // Auto-trigger grading if essay isn't graded (but not if there's already an error)
  useEffect(() => {
    if (essay && (!essay.graded || !essay.feedback) && !autoGrading && !error) {
      setAutoGrading(true);
      handleAutoGrading();
    }
  }, [essay, autoGrading, error]);

  const handleAutoGrading = async () => {
    try {
      console.log(`Auto-triggering grading for essay ${essay.id}`);
      const response = await reviewAPI.gradeTrainingEssay(authToken, essay.id);
      
      if (response.task_id) {
        setTaskId(response.task_id);
        // Poll for results
        pollForResults(response.task_id);
      } else if (response.feedback) {
        // Immediate result
        handleGradingComplete(response.feedback);
      }
    } catch (error) {
      console.error('Auto-grading failed:', error);
      
      // Check if this is a category validation error that needs admin attention
      if (error.message && error.message.includes('Invalid grading category')) {
        setError('This essay has an invalid grading category and needs admin attention. Please contact support.');
      } else if (error.message && error.message.includes('No grading category set')) {
        setError('This essay has no grading category set and needs admin attention. Please contact support.');
      } else {
        setError('Failed to grade essay automatically. Please try refreshing the page.');
      }
      setAutoGrading(false);
    }
  };

  const pollForResults = async (taskId) => {
    const maxAttempts = 30; // 5 minutes max
    let attempts = 0;
    
    const poll = async () => {
      try {
        attempts++;
        const statusResponse = await reviewAPI.getTaskStatus(authToken, taskId);
        
        if (statusResponse.status === 'completed' && statusResponse.feedback) {
          handleGradingComplete(statusResponse.feedback);
          return;
        }
        
        if (statusResponse.status === 'failed') {
          setError('Essay grading failed. Please try again.');
          setAutoGrading(false);
          return;
        }
        
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000); // Poll every 10 seconds
        } else {
          setError('Grading is taking longer than expected. Please refresh the page.');
          setAutoGrading(false);
        }
      } catch (error) {
        console.error('Polling error:', error);
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000);
        } else {
          setError('Failed to check grading status. Please refresh the page.');
          setAutoGrading(false);
        }
      }
    };
    
    poll();
  };

  const handleGradingComplete = (feedback) => {
    const feedbackIsJson = isJsonResponse(feedback);
    setIsJson(feedbackIsJson);
    setResult(feedback);
    setAutoGrading(false);
    
    if (feedbackIsJson) {
      const parsedData = parseJsonResponse(feedback);
      setJsonData(parsedData);
      setLocalJsonData(parsedData);
    } else {
      const parsed = parseLineByLine(feedback);
      setParsedFeedback(parsed);
    }
    
    // Reload essay data to get updated status
    loadEssay();
  };

  const loadEssay = async () => {
    const callId = Date.now();
    console.log(`🔄 loadEssay STARTED - Call ID: ${callId}`);
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await reviewAPI.getTrainingEssay(authToken, trainingId);
      const essayData = response && (response.essay || response);
      if (!essayData) {
        throw new Error('Essay not found');
      }
      setEssay(essayData);
      
      // Use cleaned_answer for the essay text (parsed from JSON cleaned_text by backend)
      let essayText;
      
      // FORCE: Always use cleaned_answer if it exists and has content
      if (essayData.cleaned_answer && essayData.cleaned_answer.trim().length > 0) {
        essayText = essayData.cleaned_answer;
        console.log(`🔒 FORCED to use cleaned_answer - Call ID: ${callId}`);
      } else {
        essayText = essayData.cleaned_text || '';
        console.log(`⚠️ Using cleaned_text fallback - Call ID: ${callId}`);
      }
      
      console.log(`🔍 ESSAY TEXT DEBUG - Call ID: ${callId}:`, {
        hasCleanedAnswer: !!essayData.cleaned_answer,
        cleanedAnswerLength: essayData.cleaned_answer?.length || 0,
        cleanedTextLength: essayData.cleaned_text?.length || 0,
        essayTextStart: essayText.substring(0, 100),
        essayTextTrimmedStart: essayText.trim().substring(0, 100)
      });
      
      // TEMPORARY: Alert to verify this code is running
      if (essayText.includes('{"question"')) {
        console.log(`🚨 DETECTED JSON IN ESSAY TEXT - Call ID: ${callId} - APPLYING FIX`);
        alert(`JSON detected in essay text - Call ID: ${callId} - check console for debug info`);
      }
      
      // FORCE JSON parsing check - even if it doesn't start with {
      if (essayText.includes('{"question"') || essayText.includes('"answer"')) {
        try {
          console.log(`🔧 FORCING JSON parse attempt - Call ID: ${callId}`);
          const parsed = JSON.parse(essayText);
          if (parsed.answer) {
            console.log(`✅ FORCED extraction successful - Call ID: ${callId}`);
            essayText = parsed.answer;
            alert(`FORCED JSON extraction worked - Call ID: ${callId}!`);
          } else {
            console.warn(`JSON parsed but no answer field found - Call ID: ${callId}:`, Object.keys(parsed));
          }
        } catch (e) {
          console.warn(`Failed to parse as JSON - Call ID: ${callId}:`, e);
        }
      }
      
      // Fallback: if essayText looks like JSON (starts with { after trimming), try to extract the answer
      const trimmedText = essayText.trim();
      if (trimmedText.startsWith('{')) {
        try {
          console.log(`🔧 Attempting to parse as JSON - Call ID: ${callId}...`);
          const parsed = JSON.parse(trimmedText);
          if (parsed.answer) {
            essayText = parsed.answer;
            console.log(`✅ Frontend fallback: extracted answer from JSON cleaned_text - Call ID: ${callId}`);
            alert(`Successfully extracted answer from JSON - Call ID: ${callId}!`);
          } else {
            console.warn(`JSON parsed but no answer field found - Call ID: ${callId}:`, Object.keys(parsed));
          }
        } catch (e) {
          console.warn(`Failed to parse cleaned_text as JSON - Call ID: ${callId}:`, e);
        }
      }
      
      console.log(`📝 SETTING ANSWER - Call ID: ${callId} - Length: ${essayText.length} - Start: ${essayText.substring(0, 50)}`);
      setAnswer(essayText);
      
      setCategory(essayData.grading_category || essayData.category);  // Use grading_category first, fallback to category
      setQuestion(essayData.question || `Training essay analysis for ${essayData.grading_category || essayData.category}`);  // Use actual question if available
      
      // Calculate word count from the actual essay text
      setWordCount(essayText.split(/\s+/).filter(word => word.length > 0).length);
      
      // Check if already graded
      if (essayData.graded && essayData.feedback) {
        const feedbackIsJson = isJsonResponse(essayData.feedback);
        setIsJson(feedbackIsJson);
        setResult(essayData.feedback);
        setTaskId(essayData.task_id);
        
        if (feedbackIsJson) {
          const parsedData = parseJsonResponse(essayData.feedback);
          // If we have json_grades, merge them into the feedback data
          if (essayData.json_grades) {
            parsedData.grades = essayData.json_grades;
          }
          
          // Conservative sanitization: only if exactly one paragraph and it is a QA JSON blob
          if (parsedData && Array.isArray(parsedData.paragraphs) && parsedData.paragraphs.length === 1) {
            const only = parsedData.paragraphs[0];
            if (only && typeof only.content === 'string') {
              const t = only.content.trim();
              if (t.startsWith('{') && (t.includes('"answer"') || t.includes('"question"'))) {
                try {
                  const obj = JSON.parse(t);
                  if (obj && typeof obj.answer === 'string' && obj.answer.trim().length > 0) {
                    console.log('🧹 Single-paragraph QA JSON detected → replacing content with answer text');
                    parsedData.paragraphs[0] = { ...only, content: obj.answer };
                  }
                } catch (_) {
                  // ignore parse errors; keep original content
                }
              }
            }
          }
          
          console.log('Loaded JSON feedback data:', parsedData);
          setJsonData(parsedData);
          setLocalJsonData(parsedData);
        } else {
          // Parse text-based feedback
          const parsed = parseLineByLine(essayData.feedback);
          setParsedFeedback(parsed);
        }
      }
      
    } catch (error) {
      console.error('Error loading essay:', error);
      setError('Failed to load essay. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    navigate('/');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getGradeDisplay = (originalGrade) => {
    try {
      if (typeof originalGrade === 'string' && originalGrade.startsWith('{')) {
        const gradeData = JSON.parse(originalGrade);
        return gradeData.grade || 'N/A';
      }
      return originalGrade || 'N/A';
    } catch (e) {
      return originalGrade || 'N/A';
    }
  };

  // Grade editing functions (copied from main app)
  const handleEditGrade = () => {
    setIsEditingGrade(true);
    const currentGradeText = jsonGradesToText(jsonData?.grades || {});
    setEditedGradeText(currentGradeText);
  };

  const handleSaveGrade = async () => {
    // Implementation would go here for text-based grade editing
    console.log('Grade editing not implemented for training essays');
  };

  const handleCancelGradeEdit = () => {
    setIsEditingGrade(false);
    setIsStructuredEdit(false);
    setGradeSaveStatus(null);
  };

  // Add tracking for comment ratings
  const handleCommentRatingWithTracking = async (paraIndex, fbIndex, rating) => {
    await handleCommentRatingHook(paraIndex, fbIndex, rating);
  };

  if (authLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return null; // Will redirect to login
  }

  if (loading) {
    return <div className="loading">Loading essay...</div>;
  }

  if (!essay) {
    return (
      <div>
        <div className="error">Essay not found</div>
        <button onClick={handleBackToList} className="review-button">
          Back to Essays List
        </button>
      </div>
    );
  }



  if (!essay.graded || !essay.feedback) {
    return (
      <div className="content-wrapper">
        <div className="results-container">
          <button 
            onClick={handleBackToList}
            style={{
              background: '#6b7280',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              marginBottom: '20px'
            }}
          >
            ← Back to Essays
          </button>
          
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
            <h1 style={{ margin: '0 0 16px 0', color: '#1a202c' }}>
              Training Essay - Student #{essay.id}
            </h1>
            <div style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="loading-spinner" style={{
                width: '20px',
                height: '20px',
                border: '2px solid #e2e8f0',
                borderTop: '2px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Grading this essay automatically...
            </div>
            {error && (
              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                background: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '6px',
                color: '#dc2626'
              }}>
                {error}
              </div>
            )}
          </div>
        </div>
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Helper function to render marking scheme based on category
  const renderMarkingScheme = (category, essay) => {
    // First try to get from centralized config
    const categoryConfig = getCategoryGradeConfig(category);
    
    if (categoryConfig && categoryConfig.gradeCategories) {
      return (
        <>
          {categoryConfig.gradeCategories.map(({ name, code, color, description }) => (
            <p key={name} className={`marking-scheme-${name}`} style={{ color }}>
              <strong>{code ? `${code} - ` : ''}{name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' ')}:</strong> {description}
            </p>
          ))}
        </>
      );
    }
    
    // Try to get dynamic marking scheme from actual grade data
    if (essay && essay.json_grades && typeof essay.json_grades === 'object') {
      const grades = essay.json_grades;
      
      // If we have grade data, render dynamically
      const gradeCategories = Object.entries(grades).filter(([key]) => key !== 'total');
      
      if (gradeCategories.length > 0) {
        // Generate default colors cycling through a palette
        const defaultColors = ['#e74c3c', '#3498db', '#f39c12', '#27ae60', '#9b59b6', '#e67e22'];
        
        // Provide generic but helpful descriptions
        const descriptions = {
          content: 'Critical analysis of themes, characters, techniques; depth of understanding and insight.',
          language: 'Grammar, syntax, vocabulary accuracy, and complexity of language structures.',
          analysis: 'Critical analysis of themes, characters, techniques; depth of understanding and insight.',
          expression: 'Clarity of expression, fluency, appropriate register, and effective communication of ideas.',
          cultural_context: 'Understanding of cultural, historical, and social contexts relevant to the text/film.',
          language_accuracy: 'Grammar, syntax, vocabulary accuracy, and complexity of language structures.',
          purpose: 'Clear understanding and engagement with the purpose and audience of the task.',
          coherence: 'Logical structure, clear progression of ideas, and effective organization.',
          mechanics: 'Spelling, punctuation, grammar, and technical accuracy.',
          knowledge: 'Demonstration of relevant knowledge and understanding of the subject matter.',
          engagement: 'Evidence of personal engagement and connection with the material.',
          argument: 'Clear thesis, well-developed arguments, textual evidence',
          context: 'Historical, cultural, and biographical understanding'
        };
        
        return (
          <>
            {gradeCategories.map(([gradeKey, gradeData], index) => {
              const color = defaultColors[index % defaultColors.length];
              
              // Capitalize and format the grade key
              const displayName = gradeKey.charAt(0).toUpperCase() + gradeKey.slice(1).replace(/_/g, ' ');
              
              const description = descriptions[gradeKey.toLowerCase()] || 
                `Assessment of ${displayName.toLowerCase()} skills and understanding.`;
              
              return (
                <p key={gradeKey} className={`marking-scheme-${gradeKey.toLowerCase()}`} style={{ color }}>
                  <strong>{displayName}:</strong> {description}
                </p>
              );
            })}
          </>
        );
      }
    }
    
    // Fallback to hardcoded schemes for categories without dynamic data
    if (isGCSECategory(category)) {
      return (
        <>
          <p className="marking-scheme-argument" style={{ color: '#9b59b6' }}>
            <strong>Argument:</strong> Clear thesis, well-developed arguments, textual evidence
          </p>
          <p className="marking-scheme-language" style={{ color: '#3498db' }}>
            <strong>Language:</strong> Appropriate tone, varied vocabulary, literary techniques
          </p>
          <p className="marking-scheme-context" style={{ color: '#f39c12' }}>
            <strong>Context:</strong> Historical, cultural, and biographical understanding
          </p>
          <p className="marking-scheme-mechanics" style={{ color: '#e74c3c' }}>
            <strong>Mechanics:</strong> Grammar, punctuation, spelling accuracy
          </p>
        </>
      );
    } else {
      // Default English Leaving Cert (PCLM)
      return (
        <>
          <p className="marking-scheme-purpose">
            <strong>Purpose:</strong> Have you answered all parts of the question?
          </p>
          <p className="marking-scheme-coherence">
            <strong>Coherence:</strong> Good points, appropriate quotes, well-structured paragraphs
          </p>
          <p className="marking-scheme-language">
            <strong>Language:</strong> Suitability of tone, style, varied vocabulary
          </p>
          <p className="marking-scheme-mechanics">
            <strong>Mechanics:</strong> Punctuation, spelling, syntax
          </p>
        </>
      );
    }
  };

  // Main content when essay is graded - EXACT same structure as main app
  const content = (
    <div>
      {/* Back button */}
      <button 
        onClick={handleBackToList}
        style={{
          background: '#6b7280',
          color: 'white',
          border: 'none',
          padding: '10px 16px',
          borderRadius: '6px',
          cursor: 'pointer',
          marginBottom: '20px'
        }}
      >
        ← Back to Essays
      </button>

      <h2 className="page-title">Training Essay Graded</h2>
      
      {/* Feedback Legend */}
      <div className="feedback-legend">
        {(() => {
          // First try to get from centralized config
          const categoryConfig = getCategoryGradeConfig(category);
          
          if (categoryConfig && categoryConfig.gradeCategories) {
            return (
              <div className="marking-scheme">
                <h3>Marking Scheme Explained</h3>
                {categoryConfig.gradeCategories.map(({ name, code, color, description }) => (
                  <p key={name} style={{ color }}>
                    <strong>{code ? `${code} - ` : ''}{name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' ')}:</strong> {description}
                  </p>
                ))}
              </div>
            );
          }
          
          // Fall back to dynamic marking scheme based on actual grade data
          if (essay && essay.json_grades && typeof essay.json_grades === 'object') {
            return (
              <div className="marking-scheme">
                <h3>Marking Scheme Explained</h3>
                {Object.entries(essay.json_grades).filter(([key]) => key !== 'total').map(([gradeKey, gradeData], index) => {
                  // Try to get from config first
                  const gradeConfig = categoryConfig?.gradeCategories?.find(gc => gc.name === gradeKey);
                  
                  if (gradeConfig) {
                    // Use config values
                    const displayName = gradeConfig.name.split(' ').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ');
                    const labelText = gradeConfig.code 
                      ? `${gradeConfig.code} - ${displayName}`
                      : displayName;
                    
                    return (
                      <p key={gradeKey} style={{ color: gradeConfig.color }}>
                        <strong>{labelText}:</strong> {gradeConfig.description}
                      </p>
                    );
                  } else {
                    // Fallback for categories not in config
                    const defaultColors = ['#e74c3c', '#3498db', '#f39c12', '#27ae60', '#9b59b6', '#e67e22'];
                    const color = defaultColors[index % defaultColors.length];
                    
                    // Capitalize and format the grade key
                    const displayName = gradeKey.charAt(0).toUpperCase() + gradeKey.slice(1).replace(/_/g, ' ');
                    
                    // Provide generic but helpful descriptions
                    const descriptions = {
                      content: 'Critical analysis of themes, characters, techniques; depth of understanding and insight.',
                      language: 'Grammar, syntax, vocabulary accuracy, and complexity of language structures.',
                      analysis: 'Critical analysis of themes, characters, techniques; depth of understanding and insight.',
                      expression: 'Clarity of expression, fluency, appropriate register, and effective communication of ideas.',
                      cultural_context: 'Understanding of cultural, historical, and social contexts relevant to the text/film.',
                      language_accuracy: 'Grammar, syntax, vocabulary accuracy, and complexity of language structures.',
                      purpose: 'Clear understanding and engagement with the purpose and audience of the task.',
                      coherence: 'Logical structure, clear progression of ideas, and effective organization.',
                      mechanics: 'Spelling, punctuation, grammar, and technical accuracy.',
                      knowledge: 'Demonstration of relevant knowledge and understanding of the subject matter.',
                      engagement: 'Evidence of personal engagement and connection with the material.',
                      argument: 'Clear thesis, well-developed arguments, textual evidence',
                      context: 'Historical, cultural, and biographical understanding'
                    };
                    
                    const description = descriptions[gradeKey.toLowerCase()] || 
                      `Assessment of ${displayName.toLowerCase()} skills and understanding.`;
                    
                    return (
                      <p key={gradeKey} style={{ color }}>
                        <strong>{displayName}:</strong> {description}
                      </p>
                    );
                  }
                })}
              </div>
            );
          }
          
          // Fall back to hardcoded schemes
          if (isGCSECategory(category)) {
            return (
              <div className="marking-scheme">
                <h3>Marking Scheme Explained</h3>
                <p style={{ color: '#9b59b6' }}>
                  <strong>AO1 - Argument:</strong> Clear understanding of text, answers the question directly with evidence
                </p>
                <p style={{ color: '#3498db' }}>
                  <strong>AO2 - Language:</strong> Analysis of language techniques, dramatic devices, and structure
                </p>
                <p style={{ color: '#f39c12' }}>
                  <strong>AO3 - Context:</strong> Relevant historical, social, and literary context connected to the text
                </p>
                <p style={{ color: '#e74c3c' }}>
                  <strong>AO4 - Mechanics:</strong> Accurate spelling, punctuation, and varied sentence structure
                </p>
              </div>
            );
          } else {
            return (
              <div className="marking-scheme">
                <h3>Marking Scheme Explained</h3>
                {renderMarkingScheme(category, essay)}
              </div>
            );
          }
        })()}
      </div>

      {/* Student Info Box */}
      <div className="question-box">
        <h3>Student Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <p><strong>Student:</strong> Student #{essay.id}</p>
            <p><strong>Email:</strong> [Hidden for privacy]</p>
            <p><strong>Submitted:</strong> {formatDate(essay.submitted_at)}</p>
          </div>
          <div>
            <p><strong>Subject:</strong> {essay.subject}</p>
            <p><strong>Category:</strong> {essay.grading_category || essay.category}</p>
            <p><strong>Original Grade:</strong> {getGradeDisplay(essay.original_grade)}</p>
          </div>
        </div>
      </div>

      {/* Essay Question */}
      <div className="question-box" style={{ marginTop: '20px' }}>
        <h3>Essay Question</h3>
        <div style={{ 
          whiteSpace: 'pre-wrap', 
          lineHeight: '1.6',
          fontSize: '16px',
          color: '#334155',
          backgroundColor: question ? '#e8f5e9' : '#fff3cd',
          padding: '15px',
          borderRadius: '8px',
          border: question ? '1px solid #4caf50' : '1px solid #ffc107'
        }}>
          {question || 'Essay question not available - this may be a legacy essay converted before questions were tracked.'}
        </div>
        <p style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
          Essay word count: {wordCount}
        </p>
      </div>

      {/* Analysis Container - EXACT same structure as main app */}
      <div className="analysis-container">
        <h3 className="analysis-title">Your Answer Analysis</h3>
        {feedbackType === 'inline' ? (
          isJson && jsonData ? (
            // Render JSON-based feedback - EXACTLY like main app
            <JsonFeedback
              jsonData={localJsonData || jsonData}
              category={category}
              commentRatings={commentRatings}
              handleCommentRating={handleCommentRatingWithTracking}
              userData={user}
              // Feedback editing props
              handleEditFeedback={handleEditFeedback}
              handleDeleteFeedback={handleDeleteFeedback}
              handleAddFeedback={handleAddFeedback}
              isEditingFeedback={isEditingFeedback}
              editableFeedback={editableFeedback}
              updatedFeedbackContent={updatedFeedbackContent}
              setUpdatedFeedbackContent={setUpdatedFeedbackContent}
              handleSaveEditedFeedback={handleSaveEditedFeedback}
              handleCancelEdit={handleCancelEdit}
              feedbackUpdateStatus={feedbackUpdateStatus}
              isAddingFeedback={isAddingFeedback}
              addFeedbackParaIndex={addFeedbackParaIndex}
              newFeedbackContent={newFeedbackContent}
              setNewFeedbackContent={setNewFeedbackContent}
              newFeedbackType={newFeedbackType}
              setNewFeedbackType={setNewFeedbackType}
              handleSaveNewFeedback={handleSaveNewFeedback}
              handleCancelAddFeedback={handleCancelAddFeedback}
              getFeedbackTypeOptions={getFeedbackTypeOptions}
              // Style objects
              commonInputStyle={commonInputStyle}
              commonButtonStyle={commonButtonStyle}
              secondaryButtonStyle={secondaryButtonStyle}
            />
          ) : (
            // Fallback to text-based feedback rendering
            <div>
              <h4>Feedback (Text Format)</h4>
              <div style={{
                whiteSpace: 'pre-wrap',
                padding: '16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb'
              }}>
                {result || 'No feedback available'}
              </div>
            </div>
          )
        ) : null}
      </div>

      {/* JSON Grades Section - EXACTLY like main app */}
      {isJson && jsonData && (
        <JsonGrades
          jsonData={localJsonData || jsonData}
          category={category}
          wordCount={wordCount}
          userData={user}
          isEditingGrade={isEditingGrade}
          editedGradeText={editedGradeText}
          setEditedGradeText={setEditedGradeText}
          handleEditGrade={handleEditGrade}
          handleSaveGrade={handleSaveGrade}
          handleCancelGradeEdit={handleCancelGradeEdit}
          gradeSaveStatus={gradeSaveStatus}
          editedGrades={editedGrades}
          setEditedGrades={setEditedGrades}
          isStructuredEdit={isStructuredEdit}
          setIsStructuredEdit={setIsStructuredEdit}
          taskId={taskId}
          authToken={authToken}
          API_BASE_URL={API_BASE_URL}
          localJsonData={localJsonData}
          setLocalJsonData={setLocalJsonData}
          setDisplayedGradeText={setDisplayedGradeText}
          setGradeSaveStatus={setGradeSaveStatus}
          setIsEditingGrade={setIsEditingGrade}
          toggleFAQ={() => {}} // No FAQ for training essays
        />
      )}

      {/* Action buttons */}
      <div className="action-buttons-container">
        <button onClick={handleBackToList} className="common-button secondary-button">
          Back to Essays List
        </button>
      </div>
    </div>
  );

  return (
    <div className="content-wrapper">
      <div className="results-container">
        {content}
      </div>
    </div>
  );
};

export default TrainingEssayReview; 